import { useState } from 'react';
import {
	useTranslationAdminDashboard,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader';
import { Button, Flex, Text, FlexProps } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import styled from 'styled-components';
import {
	BasicUserWithPermissionRolesRes,
	PermissionRole,
	PermissionRoleWithOwnerEnum,
} from '@dealstack/shared/lib/generated';
import { TooltipButton } from '@dealstack/frontend/shared/components/Table/MantineTable/TooltipButton/TooltipButton';
import { BasicMantineTable } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTable.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import {
	getResourceTypePermissions,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { DealSettingsView } from '@dealstack/frontend/app/pages/Project/DealDashboard/Deal.types';
import { GenericError } from '@dealstack/frontend/shared/pages/GenericError/GenericError';
import { InviteTeamMemberDrawer } from '@dealstack/frontend/shared/components/InviteTeamMemberDrawer/InviteTeamMemberDrawer';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import {
	useDeleteDealUser,
	useInviteDealUser,
} from '@dealstack/frontend/app/pages/Project/DealDetails/hooks';
import { useAppSelector } from '@dealstack/frontend/admin/hooks/redux';
import { selectUserId } from '@dealstack/frontend/app/redux/user.selectors';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { generatePath, useParams } from 'react-router-dom';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useDeal } from '@dealstack/frontend/shared/modules/deal/hooks/useDeal';
import { getDealUsers } from '@dealstack/frontend/shared/dataExchange/services/project.service';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	createUserNameColumn,
	createUserPermissionsColumn,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/UserColumn';

interface Props {
	onViewChange: (view: DealSettingsView, userId: string) => void;
}

export const DealUsersSettings = ({ onViewChange }: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const { dealId } = useParams();
	const { data: deal } = useDeal({
		dealId: dealId!,
		placeholderData: keepPreviousData,
	});
	const getDealTeamRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.DEAL_DEALID_USERS_GET, {
				dealId: dealId || '',
			}),
		],
		queryFn: () =>
			getDealUsers(
				formatUnicorn(API_PATHS.DEAL_DEALID_USERS_GET, {
					dealId: dealId || '',
				})
			),
	});
	const team = getDealTeamRequest.data;
	const tCommonUi = useTranslationCommonUi();

	const { mutate: inviteDealUser, isPending } = useInviteDealUser();
	const { mutate: deleteDealUser } = useDeleteDealUser();

	const [showInviteDrawer, setShowInviteDrawer] = useState(false);
	const dealPermissions = [
		...getResourceTypePermissions(ResourceType.DEAL),
		PermissionRoleWithOwnerEnum.OWNER,
	];

	const permissions = dealPermissions
		.map((relation) => ({
			label: tAdminDashboard(`invite_team_member.relation.${relation}`),
			value: relation,
		})) // sort by alphabetical order
		.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

	useBreadcrumbs([
		{
			name: tCommonUi('breadcrumbs.deal_settings'),
			url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.SETTINGS}`, { dealId: dealId! }),
		},
	]);

	const resendInvitation = (row: BasicUserWithPermissionRolesRes) => {
		if (!dealId) {
			throw Error('Expect dealId to be defined');
		}
		inviteDealUser(
			{
				firstName: row.firstName,
				lastName: row.lastName,
				email: row.email,
				dealId,
			},
			{
				onSuccess: () => {
					showToastV2({
						message: tAdminDashboard(
							'deal_settings.team.toast.resend_invitation.success_message'
						),
					});
				},
			}
		);
	};

	const removeUser = (userId: string) => {
		if (!dealId) {
			throw Error('Expect dealId to be defined');
		}
		deleteDealUser(
			{
				dealId,
				userId,
			},
			{
				onSuccess: async () => {
					showToastV2({
						message: tAdminDashboard(
							'deal_settings.team.toast.remove_user.success_message'
						),
					});
					await getDealTeamRequest.refetch();
				},
			}
		);
	};

	const handleDealInvite = ({ firstName, lastName, email, role }, { onSuccess }) => {
		if (!dealId) {
			throw Error('Expect dealId to be defined');
		}
		inviteDealUser(
			{
				dealId,
				firstName,
				lastName,
				email,
				permissionRoles: [role],
			},
			{
				onSuccess: async () => {
					await getDealTeamRequest.refetch();
					onSuccess?.();
				},
			}
		);
	};
	const currentUserId = useAppSelector(selectUserId);

	const columns: ExtendedColumnDef<BasicUserWithPermissionRolesRes>[] = [
		createUserNameColumn({
			header: tAdminDashboard('deal_settings.team.table.headers.name'),
			filterPlaceholder: tAdminDashboard('deal_settings.team.table.filter.name'),
			filterText: tAdminDashboard('deal_settings.team.table.filter.name'),
		}),
		createUserPermissionsColumn({
			id: 'permissions',
			t: tAdminDashboard,
			header: tAdminDashboard('deal_settings.team.table.headers.permissions'),
			filterPlaceholder: tAdminDashboard('deal_settings.team.table.filter.permissions'),
			filterText: tAdminDashboard('deal_settings.team.table.filter.permissions'),
			permissions,
		}),
		{
			cell: ({ row }) => (
				<Flex w="100%" align="center" justify="flex-end">
					{row.original.active ? null : (
						<Button
							variant="ghost"
							onClick={(e) => {
								e.stopPropagation();
								resendInvitation(row.original);
							}}
						>
							{tAdminDashboard('deal_settings.team.buttons.resend_invitation')}
						</Button>
					)}
					{currentUserId !== row.original.id && (
						<Button
							variant="subtle"
							color="red"
							onClick={(e) => {
								e.stopPropagation();
								removeUser(row.original.id);
							}}
						>
							{tAdminDashboard('deal_settings.team.buttons.remove_user')}
						</Button>
					)}
				</Flex>
			),
			header: '',
			id: 'actions',
			showFilter: false,
			meta: {
				isGrow: true,
			},
		},
	];

	if (!dealId) {
		return <GenericError />;
	}

	return (
		<Flex direction="column" h="100%" w="100%" style={{ overflow: 'auto' }}>
			<ContainerWithHeader
				title={
					<StyledSubBreadcrumbs align="center" gap="0.5rem">
						<Text variant="p-sm">{deal?.name}</Text>
						<Icon variant="backslash" size="vlg" />
						<Text variant="p-sm">
							{tAdminDashboard('deal_settings.labels.deal_team')}
						</Text>
					</StyledSubBreadcrumbs>
				}
			>
				{!!team?.length && (
					<BasicMantineTable<BasicUserWithPermissionRolesRes>
						columns={columns}
						isLoading={getDealTeamRequest.isLoading}
						data={team}
						mantineTableBodyRowProps={({ row }) => ({
							onClick: () => {
								onViewChange(DealSettingsView.User, row.original.id);
							},
							style: {
								cursor: 'pointer',
							},
						})}
						customActions={
							<TooltipButton
								leftSection={<Icon variant="plus" size="vlg" />}
								text={tAdminDashboard('deal_settings.team.buttons.add_new')}
								onClick={() => setShowInviteDrawer(true)}
							/>
						}
					/>
				)}
			</ContainerWithHeader>
			{deal?.id && (
				<InviteTeamMemberDrawer
					isOpen={showInviteDrawer}
					isLoading={isPending}
					onInvite={handleDealInvite}
					setIsOpen={setShowInviteDrawer}
					permissions={
						permissions.filter(
							(permission) => permission.value !== PermissionRoleWithOwnerEnum.OWNER
						) as { label: string; value: PermissionRole }[]
					}
					description={tAdminDashboard('invite_member_drawer.description')}
					roleLabel={tAdminDashboard('invite_member_drawer.form.role_field.label')}
				/>
			)}
		</Flex>
	);
};

const StyledSubBreadcrumbs = styled(Flex)<FlexProps>`
	.mantine-Text-root:not(:last-child) {
		color: ${({ theme }) => theme.v3.colors.dark[0]};
	}
`;
